import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownToLine, ArrowRight, ArrowUpFromLine, CalendarClock, Check, CircleAlert, Clock3, Infinity as InfinityIcon, Percent, PlaneTakeoff, RefreshCw,
  ShieldCheck, Target, TrendingUp, type LucideIcon
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { photoBackground } from "../components/Cards";
import { ContributeDialog } from "../components/ContributeDialog";
import { GoalDialog } from "../components/GoalDialog";
import { FundInfo, KuspitNotice } from "../components/Disclosures";
import { Modal } from "../components/Modal";
import { usePageHeader } from "../components/shell/PageHeaderContext";
import { Sparkline, type SeriesPoint } from "../components/Sparkline";
import { SpeiWithdrawalDialog } from "../components/SpeiWithdrawalDialog";
import { Spinner } from "../components/Spinner";
import { TripMilestones } from "../components/TripMilestones";
import { WalletDocumentsCard } from "../components/WalletDocumentsCard";
import { KycNotice } from "../components/KycNotice";
import { apiRequest } from "../lib/api";
import { formatCalendarDate, formatShortDate, formatTimestamp, money, relativeTime } from "../lib/format";
import { useLibreWallet, useProducts, useTrip } from "../lib/queries";
import type { ApiEnvelope, TransactionStatus, Wallet, WalletTransaction } from "../types";
import { APARTADO_STATUS_LABELS, mxn } from "../lib/reservations";
import { DAILY_AVAILABILITY_BENEFIT, KUSPIT_WALLET_NOTICE } from "@aventuria/client-core/disclosures";

const monthLabel = new Intl.DateTimeFormat("es-MX", { month: "short" });
const dayLabel = new Intl.DateTimeFormat("es-MX", { day: "numeric", month: "short" });

const RANGES = [
  { id: "1M", label: "1M", months: 1 },
  { id: "6M", label: "6M", months: 6 },
  { id: "1Y", label: "1Y", months: 12 }
] as const;
type RangeId = (typeof RANGES)[number]["id"];

const BENEFIT_ICONS: Record<string, LucideIcon> = { "calendar-clock": CalendarClock, infinity: InfinityIcon, percent: Percent, "shield-check": ShieldCheck, target: Target };
const DEFAULT_BENEFITS = [
  { icon: "infinity", title: "Sin monto mínimo", copy: "Aporta lo que quieras, cuando quieras" },
  DAILY_AVAILABILITY_BENEFIT
];
const HERO_SHADE = "linear-gradient(155deg, rgba(0,37,87,0.55) 0%, rgba(1,121,226,0.35) 55%, rgba(0,20,45,0.75) 100%)";

/** Wallet Libre dashboard (Desktop · Wallet Libre · Main). */
export function WalletLibrePage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [syncing, setSyncing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Arriving from a trip's "Crear meta" carries a success message (B3).
  useEffect(() => {
    const state = location.state as { notice?: string } | null;
    if (state?.notice) { setNotice(state.notice); window.history.replaceState({}, ""); }
  }, [location.state]);
  const [range, setRange] = useState<RangeId>("6M");
  const [conditionsOpen, setConditionsOpen] = useState(false);
  const { wallet, isLoading, isError, refetch } = useLibreWallet();
  const products = useProducts();
  const trip = useTrip();
  const settings = products.data?.settings.wallet_libre;
  usePageHeader("Wallet Libre", settings?.subtitle ?? "Ahorro flexible · sin destino fijo");

  const series = useMemo(() => balanceSeries(wallet, range), [wallet, range]);

  const applyWallet = (next: Wallet) => {
    queryClient.setQueryData(["wallets"], (current: ApiEnvelope<{ wallets: Wallet[] }> | undefined) => ({
      data: { wallets: (current?.data.wallets ?? []).map((item) => (item.id === next.id ? next : item)).concat(current?.data.wallets.some((item) => item.id === next.id) ? [] : [next]) }
    }));
  };

  const refresh = async () => {
    if (!wallet) return;
    setSyncing(true); setNotice(null);
    try {
      const response = await apiRequest<ApiEnvelope<{ wallet: Wallet; synchronization: { status: "synced" | "fresh"; asOf: string } }>>(`/api/v1/wallets/${wallet.id}/synchronize`, { method: "POST" });
      applyWallet(response.data.wallet);
      setNotice("Saldo actualizado.");
    } catch (cause) { setNotice(cause instanceof Error ? cause.message : "No pudimos consultar tu saldo en Kuspit."); }
    finally { setSyncing(false); }
  };
  const completed = (_transaction: WalletTransaction) => { void queryClient.invalidateQueries({ queryKey: ["wallets"] }); setNotice("Operación registrada."); };

  if (isLoading) return <div className="page-state"><Spinner label="Cargando wallet" /><span>Cargando wallet</span></div>;
  if (isError) return <div className="page-state error"><CircleAlert aria-hidden="true" /><strong>No pudimos cargar tu wallet</strong><button className="secondary-button" onClick={() => void refetch()}>Reintentar</button></div>;
  if (!wallet) return <Navigate to="/wallet/libre/nueva" replace />;

  const goal = wallet.goal;
  const benefits = settings?.benefits?.length ? settings.benefits : DEFAULT_BENEFITS;

  return (
    <div className="page-container">
      <div className="wallet-grid">
        <div className="wallet-column">
          <section className="wallet-hero" style={photoBackground(goal?.photoUrl, HERO_SHADE, "wide")} aria-labelledby="balance-title">
            <div className="hero-top">
              <div className="hero-dream">
                <span className="hero-eyebrow">Tu sueño</span>
                <h2>{goal?.title ?? "Tu próxima aventura"}</h2>
                <div className="hero-pills">
                  {goal?.amount || goal?.date ? (
                    <span className="glass-pill" translate="no">
                      {goal.amount ? `Meta ${money.format(goal.amount)}` : null}{goal.amount && goal.date ? " · " : null}{goal.date ? formatCalendarDate(goal.date) : null}
                    </span>
                  ) : null}
                  <GoalDialog wallet={wallet} onSaved={applyWallet} />
                </div>
              </div>
              <div className="hero-side">
                <StatusPill status={wallet.status} />
              </div>
            </div>
            <div className="hero-bottom">
              <div>
                <span className="hero-label" id="balance-title">Ahorrado</span>
                <strong className="hero-amount">{splitAmount(wallet.balance.available)}</strong>
                {wallet.balance.reserved > 0 && (
                  <div className="balance-split" translate="no">
                    <span><small>Disponible</small><strong>{money.format(wallet.balance.free)}</strong></span>
                    <span><small>Apartado para viajes</small><strong>{money.format(wallet.balance.reserved)}</strong></span>
                  </div>
                )}
                {wallet.balance.accruedInterest > 0 ? (
                  <div className="hero-yield"><TrendingUp size={15} aria-hidden="true" /><span translate="no">+{money.format(wallet.balance.accruedInterest)} ganado por rendimiento</span></div>
                ) : (
                  <div className="hero-yield muted"><TrendingUp size={15} aria-hidden="true" /><span>Tu rendimiento aparecerá aquí</span></div>
                )}
                {goal?.progress !== null && goal?.progress !== undefined && (
                  <div className="goal-progress" aria-label={`${Math.round(goal.progress * 100)}% de tu meta`}>
                    <div className="goal-bar"><span style={{ width: `${Math.round(goal.progress * 100)}%` }} /></div>
                    <span translate="no">{Math.round(goal.progress * 100)}% de tu meta · faltan {money.format(goal.remaining ?? 0)}</span>
                  </div>
                )}
              </div>
              <div className="hero-actions">
                <ContributeDialog walletId={wallet.id} onCompleted={completed} trigger={<button type="button" className="hero-button solid">+ Aportar</button>} />
                <SpeiWithdrawalDialog walletId={wallet.id} maxAmount={wallet.balance.free} onCompleted={completed} trigger={<button type="button" className="hero-button ghost">- Retirar</button>} />
              </div>
            </div>
            <div className="hero-footer">
              <span>{syncCopy(wallet)}</span>
              <button className="icon-button inverse" onClick={() => void refresh()} disabled={syncing} aria-label="Actualizar saldo" title="Actualizar saldo">
                <RefreshCw size={18} className={syncing ? "spin" : ""} aria-hidden="true" />
              </button>
            </div>
          </section>

          {notice && <div className="inline-notice" role="status"><Check size={17} aria-hidden="true" />{notice}</div>}
          <KycNotice wallet={wallet} />
          {wallet.investment.openOrders.length > 0 && (
            <div className="inline-notice info" role="status">
              <Clock3 size={17} aria-hidden="true" />
              <span translate="no">{wallet.investment.openOrders.map((order) => `${order.side === "buy" ? "Inversión" : "Venta"} de ${mxn(order.amount)} en ${order.fund.label} en proceso`).join(" · ")}</span>
            </div>
          )}

          <section className="card growth-card" aria-labelledby="growth-title">
            <div className="growth-head">
              <div>
                <span className="rail-title" id="growth-title">Crecimiento · {rangeCopy(range)}</span>
                <div className={`growth-value ${series.change < 0 ? "down" : "up"}`} translate="no">
                  {series.change >= 0 ? "+" : "−"}{money.format(Math.abs(series.change))}
                  {series.points.length > 1 && <TrendingUp size={16} aria-hidden="true" />}
                </div>
              </div>
              <div className="range-tabs" role="group" aria-label="Periodo del gráfico">
                {RANGES.map((option) => (
                  <button key={option.id} type="button" className={`range-tab${range === option.id ? " active" : ""}`} aria-pressed={range === option.id} onClick={() => setRange(option.id)}>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            {series.points.length > 1 ? (
              <>
                <Sparkline points={series.points} label={`Saldo de los últimos ${rangeCopy(range)}`} />
                <div className="sparkline-axis">{series.axis.map((tick, index) => <span key={`${tick}-${index}`}>{tick}</span>)}</div>
              </>
            ) : (
              <div className="growth-empty"><Clock3 size={22} aria-hidden="true" /><span>Tu crecimiento aparecerá en cuanto Kuspit reporte tu saldo.</span></div>
            )}
          </section>

          <section className="benefit-grid" aria-label="Beneficios">
            {benefits.map((benefit) => {
              const Icon = BENEFIT_ICONS[benefit.icon] ?? ShieldCheck;
              return (
                <article className="benefit-card" key={benefit.title}>
                  <span className="benefit-icon"><Icon size={17} aria-hidden="true" /></span>
                  <strong>{benefit.title}</strong>
                  <p>{benefit.copy}</p>
                </article>
              );
            })}
          </section>
          {settings?.conditionsUrl
            ? <a className="text-link" href={settings.conditionsUrl} target="_blank" rel="noreferrer">Ver condiciones de inversión <ArrowRight size={14} aria-hidden="true" /></a>
            : <button type="button" className="text-link" onClick={() => setConditionsOpen(true)}>Ver condiciones de inversión <ArrowRight size={14} aria-hidden="true" /></button>}
          <Modal open={conditionsOpen} onOpenChange={setConditionsOpen} label="Condiciones de inversión">
            <div className="dialog-content">
              <span className="page-eyebrow">Wallet Libre</span>
              <h2>Condiciones de inversión</h2>
              <div className="dialog-form conditions">
                <p>El rendimiento es variable y está sujeto a las condiciones del mercado: no garantizamos una tasa determinada. Se acredita conforme lo reporta la institución que resguarda los fondos.</p>
                <p>Sin monto mínimo y sin penalización por mover la fecha o cambiar la meta de tu sueño.</p>
                <p>{KUSPIT_WALLET_NOTICE}</p>
                <button type="button" className="secondary-button full" onClick={() => setConditionsOpen(false)}>Entendido</button>
              </div>
            </div>
          </Modal>
        </div>

        <div className="wallet-rail">
          <section className="card actions-card" aria-labelledby="actions-title">
            <h2 id="actions-title">Acciones rápidas</h2>
            <div className="wallet-actions">
              <ContributeDialog walletId={wallet.id} onCompleted={completed} trigger={<button type="button" className="action-button primary"><ArrowDownToLine size={18} aria-hidden="true" /> Aportar</button>} />
              <SpeiWithdrawalDialog walletId={wallet.id} maxAmount={wallet.balance.free} onCompleted={completed} trigger={<button type="button" className="action-button"><ArrowUpFromLine size={18} aria-hidden="true" /> Retirar</button>} />
            </div>
          </section>

          <button type="button" className="nudge-card" onClick={() => navigate("/explorar")}>
            <span><PlaneTakeoff size={21} aria-hidden="true" /></span>
            <div>
              <strong>¿Listo para tu aventura?</strong>
              <small>Convierte tu ahorro en un viaje con destino y fechas.</small>
            </div>
            <ArrowRight size={17} aria-hidden="true" />
          </button>

          {trip.data && <TripMilestones trip={trip.data} saved={wallet.balance.available} compact />}

          <section className="card apartados-card" aria-labelledby="apartados-title">
            <span className="rail-title" id="apartados-title">Tus Apartados</span>
            {wallet.apartados.length ? wallet.apartados.map((apartado) => (
              <button type="button" key={apartado.id} className="apartado-row" onClick={() => navigate(`/apartados/${apartado.id}`)}>
                <div><strong>Apartado para {apartado.tripName}</strong><span>{APARTADO_STATUS_LABELS[apartado.status]} · sin retiros</span></div>
                <span className="apartado-row-amount" translate="no">{money.format(apartado.balance)}<small>de {money.format(apartado.goalAmount)}</small></span>
              </button>
            )) : <p className="rail-empty">Sin Apartados. Solicita la reservación de un viaje y destina parte de tu saldo a él.</p>}
            <button type="button" className="link-button" onClick={() => navigate("/solicitudes")}>Mis solicitudes <ArrowRight size={13} aria-hidden="true" /></button>
          </section>

          <section className="card investment-card" aria-labelledby="investment-title">
            <span className="rail-title" id="investment-title">Cómo se invierte tu dinero</span>
            {wallet.investment.enabled ? <FundInfo /> : <p>Tu dinero permanece como efectivo en tu contrato de Kuspit.</p>}
            {wallet.investment.recentOrders.length > 0 && (
              <ul className="order-list">
                {wallet.investment.recentOrders.slice(0, 3).map((order) => <li key={order.id}><span>{order.side === "buy" ? "Compra" : "Venta"} · {order.fund.label}</span><strong translate="no">{mxn(order.amount)}</strong><small>{order.status === "assigned" ? "Ejecutada" : order.status === "failed" || order.status === "cancelled" ? "No ejecutada" : order.status === "manual_review" ? "En revisión" : "En proceso"}</small></li>)}
              </ul>
            )}
          </section>

          <WalletDocumentsCard wallet={wallet} />

          <section className="card activity-card" aria-labelledby="activity-title">
            <span className="rail-title" id="activity-title">Actividad reciente</span>
            {wallet.transactions.length ? wallet.transactions.slice(0, 3).map((transaction) => (
              <div className="activity-row" key={transaction.id}>
                <div>
                  <strong>{transaction.description}</strong>
                  <span>{formatShortDate(transaction.createdAt)}</span>
                </div>
                <span className={`activity-amount ${transaction.signedAmount < 0 ? "negative" : "positive"}`} translate="no">
                  {transaction.signedAmount > 0 ? "+" : ""}{money.format(transaction.signedAmount)}
                </span>
              </div>
            )) : <p className="rail-empty">Sin movimientos todavía.</p>}
          </section>
        </div>
      </div>

      <section className="movements-section">
        <div className="section-heading">
          <div><span className="page-eyebrow">Actividad</span><h2>Movimientos</h2></div>
          <span>{wallet.transactions.length} registros</span>
        </div>
        {wallet.transactions.length ? (
          <div className="movement-list">
            {wallet.transactions.map((transaction) => <Movement key={transaction.id} transaction={transaction} />)}
          </div>
        ) : (
          <div className="empty-state"><Clock3 size={28} aria-hidden="true" /><strong>Aún no hay movimientos</strong><span>Tus operaciones aparecerán aquí.</span></div>
        )}
      </section>

      <KuspitNotice />
    </div>
  );
}

function Movement({ transaction }: { transaction: WalletTransaction }) {
  const Icon = transaction.signedAmount < 0 ? ArrowUpFromLine : ArrowDownToLine;
  return (
    <article className="movement-row">
      <div className={`movement-icon ${transaction.signedAmount < 0 ? "out" : "in"}`}><Icon size={19} aria-hidden="true" /></div>
      <div className="movement-copy"><strong>{transaction.description}</strong><span>{formatTimestamp(transaction.createdAt)}</span></div>
      <div className={`movement-status ${transaction.status}`}><StatusLabel status={transaction.status} /><span>{statusCopy(transaction.status)}</span></div>
      <div className={`movement-amount ${transaction.signedAmount < 0 ? "negative" : "positive"}`} translate="no">
        <strong>{transaction.signedAmount > 0 ? "+" : ""}{money.format(transaction.signedAmount)}</strong><span>{transaction.currency}</span>
      </div>
    </article>
  );
}

function StatusPill({ status }: { status: Wallet["status"] }) {
  return (
    <span className={`hero-pill ${status}`}>
      <i aria-hidden="true" />
      {status === "active" ? "Wallet activa" : status === "pending" ? "Wallet pendiente" : "Wallet restringida"}
    </span>
  );
}
function StatusLabel({ status }: { status: TransactionStatus }) {
  if (status === "settled") return <Check size={16} aria-label="Confirmado" />;
  if (status === "failed") return <CircleAlert size={16} aria-label="No completado" />;
  return <Clock3 size={16} aria-label="En proceso" />;
}
function statusCopy(status: TransactionStatus) { return ({ settled: "Confirmado", pending: "Pendiente", processing: "Procesando", failed: "No completado" })[status]; }

/**
 * The line under the balance. The figure is Kuspit's, so what matters is when Kuspit last said it
 * and whether the latest attempt to ask failed — never a claim that a local number "matches".
 */
function syncCopy(wallet: Wallet): string {
  const parts: string[] = [];
  if (wallet.balance.source === "unavailable") parts.push(wallet.synchronization.lastError ? "No pudimos consultar tu saldo en Kuspit" : "Consultando tu saldo en Kuspit");
  else if (wallet.synchronization.lastError) parts.push(`Saldo de Kuspit ${wallet.balance.asOf ? `al ${formatTimestamp(wallet.balance.asOf)}` : "anterior"} · sin respuesta en la última consulta`);
  else if (wallet.synchronization.lastSyncedAt) parts.push(`Saldo de Kuspit · actualizado ${relativeTime(wallet.synchronization.lastSyncedAt)}`);
  else parts.push("Saldo de Kuspit");
  if (wallet.balance.pendingIn > 0) parts.push(`${money.format(wallet.balance.pendingIn)} por confirmar`);
  if (wallet.balance.pendingOut > 0) parts.push(`${money.format(wallet.balance.pendingOut)} en camino`);
  return parts.join(" · ");
}

/**
 * The balance over time as Kuspit reported it (one point per day), windowed to the selected range.
 * The window opens at the last figure known before it (or the first figure ever, when Kuspit's
 * history starts inside the window) and closes at the latest figure, so the curve and the "+/-"
 * headline are Kuspit's numbers and nothing is derived from the movement list.
 */
function balanceSeries(wallet: Wallet | undefined, range: RangeId): { points: SeriesPoint[]; change: number; axis: string[] } {
  const empty = { points: [] as SeriesPoint[], change: 0, axis: [] as string[] };
  if (!wallet) return empty;
  const points: SeriesPoint[] = wallet.balanceHistory
    .map((entry) => ({ at: new Date(entry.at).getTime(), value: entry.available }))
    .filter((point) => Number.isFinite(point.at))
    .sort((left, right) => left.at - right.at);
  if (!points.length) return empty;

  const months = RANGES.find((option) => option.id === range)?.months ?? 6;
  const now = Date.now();
  const from = new Date(now);
  from.setMonth(from.getMonth() - months);
  const fromTime = from.getTime();

  // The chart's time domain is the SELECTED RANGE, not the data's extent, so 1M / 6M / 1Y really
  // rescale the timeline.
  const windowed = points.filter((point) => point.at > fromTime && point.at <= now);
  const previous = [...points].reverse().find((point) => point.at <= fromTime);
  const openingValue = previous?.value ?? points[0]!.value;
  const closingValue = windowed.length ? windowed[windowed.length - 1]!.value : openingValue;
  const visible = [{ at: fromTime, value: openingValue }, ...windowed, { at: now, value: closingValue }];

  return { points: visible, change: closingValue - openingValue, axis: axisLabels(visible) };
}

const AXIS_MONTH_THRESHOLD = 75 * 86_400_000;

function axisLabels(points: SeriesPoint[]) {
  const start = points[0]!.at;
  const span = points[points.length - 1]!.at - start;
  const format = span < AXIS_MONTH_THRESHOLD ? dayLabel : monthLabel;
  const ticks = 6;
  const labels: string[] = [];
  let previous = "";
  for (let index = 0; index < ticks; index += 1) {
    const at = start + (index / (ticks - 1)) * span;
    const text = format.format(new Date(at)).replace(".", "").toUpperCase();
    labels.push(text === previous ? "" : text);
    if (text !== previous) previous = text;
  }
  return labels;
}

function rangeCopy(range: RangeId) {
  return range === "1M" ? "1 mes" : range === "6M" ? "6 meses" : "12 meses";
}

/**
 * Renders the cents at a smaller weight, as the design hero does. Both parts are wrapped in
 * elements (never a bare text node) and the amount is `translate="no"`: page translators rewrite
 * inline text runs and were dropping the integer part of the figure.
 */
function splitAmount(value: number) {
  const formatted = money.format(value);
  const separator = formatted.lastIndexOf(".");
  if (separator === -1) return <span translate="no">{formatted}</span>;
  return (
    <span translate="no">
      <span className="integer">{formatted.slice(0, separator)}</span>
      <span className="cents">{formatted.slice(separator)}</span>
    </span>
  );
}

export const walletHelpers = { statusCopy, balanceSeries, syncCopy };
